import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import cn from '@/utils/cn';
import ThemeSelector from '../ThemeSelector';
import SPAAccountMenu from './SPAAccountMenu';
import SPABrandLink from './SPABrandLink';
import navSections, { visibleNavSections } from './navSections';
import { useSPAUser } from './SPAUser';
import { childrenClass, railItemClass, subItemClass } from './railClasses';
import SPANavSwitcherControl from './SPANavSwitcherControl';
import SPARail from './SPARail';
import { SPAThemeScope } from './SPAThemeScope';

// AIDEV-NOTE: The sysadmin SPA's left nav — one rail, two states.
// At rest it is the app tree; a page that registers a SPANavSlot
// drills the same rail into that page's own content, with a back header below the brand row.
// A stack, not a tree: each level gets the full rail width, nothing nests three deep.
// The rail is the whole chrome: logo on top (way home), theme + account pinned at the bottom —
// there is no top bar on rail pages, only on the rail-less welcome page.

export interface SPANavSwitcherOption {
  label: string;
  value: string;
}

// Entity drills (a program, later a subscriber) get lateral moves in the header: the title becomes
// a ▾ button opening a searchable list. Pages must keep this object referentially stable (useMemo)
// or the slot effect re-registers every render.
export interface SPANavSwitcher {
  loading?: boolean;
  onSelect: (value: string) => void;
  options: SPANavSwitcherOption[];
  placeholder: string;
  value: string;
}

export interface SPANavPageHeader {
  // Tints the brand strip with the entity's color (a program's), the way its unit headers are.
  accentColor?: string | null;
  backLabel: string;
  backTo: string;
  switcher?: SPANavSwitcher;
  // May be a node (e.g. a loading skeleton) — like the switcher, keep it referentially stable.
  title: React.ReactNode;
}

interface SPANavContextValue {
  pageHeader: SPANavPageHeader | null;
  setPageHeader: (header: SPANavPageHeader | null) => void;
  setSlotEl: (el: HTMLElement | null) => void;
  slotEl: HTMLElement | null;
}

const NAV_THEME_CLASS = 'spa-theme-dark';
const noop = () => {};

const SPANavContext = createContext<SPANavContextValue>({
  pageHeader: null,
  setPageHeader: noop,
  setSlotEl: noop,
  slotEl: null,
});

// For rails other than SPANav that host a page's SPANavSlot (the legacy drill rail).
export const useSPANav = () => useContext(SPANavContext);

interface SPANavProviderProps {
  children: React.ReactNode;
}

export const SPANavProvider = ({ children }: SPANavProviderProps) => {
  const [pageHeader, setPageHeader] = useState<SPANavPageHeader | null>(null);
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);

  const value = useMemo(
    () => ({ pageHeader, setPageHeader, setSlotEl, slotEl }),
    [pageHeader, slotEl],
  );

  return <SPANavContext.Provider value={value}>{children}</SPANavContext.Provider>;
};

interface SPANavSlotProps {
  children: React.ReactNode;
  header: SPANavPageHeader;
}

// Mount a page's rail content into the shell's rail. The children render here in the page's own
// tree (state and context intact) and portal into the rail's slot element.
export const SPANavSlot = ({ children, header }: SPANavSlotProps) => {
  const { setPageHeader, slotEl } = useContext(SPANavContext);
  const {
    accentColor, backLabel, backTo, switcher, title,
  } = header;

  useEffect(() => {
    setPageHeader({
      accentColor, backLabel, backTo, switcher, title,
    });
    return () => setPageHeader(null);
  }, [accentColor, backLabel, backTo, setPageHeader, switcher, title]);

  if (!slotEl) return null;
  return createPortal(children, slotEl);
};

export { railItemClass } from './railClasses';

const isItemActive = (to: string, pathname: string) => {
  if (to === '/content') return pathname === '/content' || pathname.startsWith('/content/programs');
  if (to === '/langfuse') return pathname === '/langfuse';
  return pathname === to || pathname.startsWith(`${to}/`);
};

interface SPANavProps {
  signOutPath: string;
  supportPath: string;
  userName: string;
}

export const SPANav = ({ signOutPath, supportPath, userName }: SPANavProps) => {
  const { pageHeader, setSlotEl } = useContext(SPANavContext);
  const { pathname } = useLocation();
  const user = useSPAUser();
  const [collapsed, setCollapsed] = useState(false);

  const slotRef = useCallback((el: HTMLElement | null) => setSlotEl(el), [setSlotEl]);

  // On a phone-ish screen the rail and the content can't share the width — trade the rail for the
  // page as soon as a destination is picked.
  const collapseOnSmallScreens = () => {
    if (window.matchMedia?.('(max-width: 767px)').matches) setCollapsed(true);
  };

  const switcher = pageHeader?.switcher;
  const accentColor = pageHeader?.accentColor;

  const backLink = pageHeader && (
    <Link
      className={cn(
        '-tw-mx-1.5 tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-rounded-md tw-px-1.5 tw-py-1',
        'tw-text-sm tw-font-bold tw-text-spa-heading tw-no-underline',
        'hover:tw-bg-spa-bg-tertiary hover:tw-no-underline hover:tw-text-spa-heading',
        switcher && 'tw-shrink-0',
      )}
      to={pageHeader.backTo}
    >
      <span aria-hidden="true" className="tw-shrink-0 tw-font-normal tw-text-spa-accent-text">‹</span>
      <span className="tw-shrink-0 tw-whitespace-nowrap tw-font-normal tw-text-spa-tertiary">
        {switcher ? pageHeader.backLabel : `${pageHeader.backLabel} ·`}
      </span>
      {!switcher && <span className="tw-min-w-0 tw-truncate">{pageHeader.title}</span>}
    </Link>
  );

  const drillHeader = pageHeader && (
    // With a switcher the header stacks: the crumb row, then the full title as the switcher
    // button — wrapping, not truncating, so the entity's name is always readable and pinned.
    <div className={cn('tw-min-w-0', switcher ? 'tw-relative tw-flex tw-flex-col' : 'tw-flex tw-items-center')}>
      {backLink}
      {switcher && <SPANavSwitcherControl closeOn={pathname} switcher={switcher} title={pageHeader.title} />}
    </div>
  );

  // The rail top reads as two zones: the brand strip (logo + collapse chevron over a divider,
  // mirroring the footer) and, when drilled, the context block (crumb tight above the title).
  const header = <SPABrandLink to="/" />;

  const footer = (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-1">
      <SPAAccountMenu className="tw-flex-1" signOutPath={signOutPath} userName={userName} />
      <ThemeSelector />
    </div>
  );

  // The nav is always dark; the scope carries that to its portalled menus (theme, account).
  return (
    <SPAThemeScope className={NAV_THEME_CLASS}>
      <SPARail
        accentColor={accentColor}
        className={cn(NAV_THEME_CLASS, 'tw-rounded-tr-none')}
        collapsed={collapsed}
        collapseLabel="Collapse navigation"
        expandLabel="Expand navigation"
        footer={footer}
        header={header}
        headerClassName={cn(
          'tw-items-center tw-pb-3',
          'tw-border-0 tw-border-b tw-border-solid tw-border-spa-border',
        )}
        subheader={drillHeader}
        onCollapsedChange={setCollapsed}
      >
        <div ref={slotRef} className={cn('tw-flex tw-flex-col', !pageHeader && 'tw-hidden')} />
        {!pageHeader && (
          <nav aria-label="Sysadmin navigation" className="tw-p-2">
            {visibleNavSections(navSections(supportPath), user).map(section => (
              <div key={section.label}>
                <div
                  className={cn(
                    'tw-px-2 tw-pb-1 tw-pt-3 tw-text-[10px] tw-font-semibold',
                    'tw-uppercase tw-tracking-wider tw-text-spa-tertiary',
                  )}
                >
                  {section.label}
                </div>
                {section.items.map((item) => {
                  const itemClass = item.sub ? subItemClass : railItemClass;
                  const link = item.to ? (
                    <Link
                      className={itemClass(isItemActive(item.to, pathname))}
                      to={item.to}
                      onClick={collapseOnSmallScreens}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <a className={itemClass(false)} href={item.href}>
                      {item.label}
                      <span aria-hidden="true" className="tw-ml-auto tw-text-xs tw-text-spa-tertiary">↗</span>
                    </a>
                  );

                  return item.sub
                    ? <div key={item.label} className={childrenClass}>{link}</div>
                    : <React.Fragment key={item.label}>{link}</React.Fragment>;
                })}
              </div>
            ))}
          </nav>
        )}
      </SPARail>
    </SPAThemeScope>
  );
};
